import React, {Fragment, useEffect, useMemo, useState} from 'react';
import MovieCard from '../../components/MovieCard/index';
import FiltersLabel from '../../components/FiltersLabel';
import MovieThumb from '../../components/MovieThumb/index';
import {NavLink, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {fetchMovies} from '../../slices/movies.slice';
import Loading from '../../components/Loading';
import {Button, ButtonGroup} from 'reactstrap';
import {isAdmin} from '../../utils/admin';
import PageHeader from '../../components/PageHeader';

const Movies = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const user = useSelector(state => state.auth.user);
    const { list: movies, status, pagination } = useSelector((state) => state.movies);
    const [displayList, setDisplayList] = useState(true);

    const parseURLFilters = () => {
        const statusParam = searchParams.get('status');
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');
        const withoutVersionsParam = searchParams.get('withoutVersions');
        const withoutPostersParam = searchParams.get('withoutPosters');

        return {
            status: statusParam ? statusParam.split(',') : [],
            search: searchParam || '',
            page: pageParam ? parseInt(pageParam, 10) : 1,
            withoutVersions: withoutVersionsParam === 'true',
            withoutPosters: withoutPostersParam === 'true',
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();

        if (newFilters.status.length > 0) {
            params.set('status', newFilters.status.join(','));
        }
        if (newFilters.search) {
            params.set('search', newFilters.search);
        }
        if (newFilters.page && newFilters.page !== 1) {
            params.set('page', newFilters.page);
        }
        if (newFilters.withoutVersions) {
            params.set('withoutVersions', 'true');
        }
        if (newFilters.withoutPosters) {
            params.set('withoutPosters', 'true');
        }

        setSearchParams(params);
    };

    const handleSearch = event => {
        event.preventDefault();
        const query = event.target.value;
        const newFilters = {
            ...filterBy,
            search: query,
            page: 1,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const toggleStatus = (statusValue) => {
        setFilterBy((prevFilterBy) => {
            const isSelected = prevFilterBy.status.includes(statusValue);
            const newFilters = {
                ...prevFilterBy,
                status: isSelected
                    ? prevFilterBy.status.filter((item) => item !== statusValue)
                    : [...prevFilterBy.status, statusValue],
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const toggleWithoutVersions = () => {
        setFilterBy((prevFilterBy) => {
            const newFilters = {
                ...prevFilterBy,
                withoutVersions: !prevFilterBy.withoutVersions,
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const toggleWithoutPosters = () => {
        setFilterBy((prevFilterBy) => {
            const newFilters = {
                ...prevFilterBy,
                withoutPosters: !prevFilterBy.withoutPosters,
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const areFiltersActive = useMemo(() => {
        const { status, search, withoutVersions, withoutPosters } = filterBy;
        return status.length > 0 || search.length > 0 || withoutVersions || withoutPosters;
    }, [filterBy]);

    const clearFilters = () => {
        const newFilters = {
            status: [],
            search: '',
            page: 1,
            withoutVersions: false,
            withoutPosters: false,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handlePageChange = (newPage) => {
        const newFilters = {
            ...filterBy,
            page: newPage,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    useEffect(() => {
        dispatch(fetchMovies(filterBy));
    }, [dispatch, filterBy]);

    const isLoading = ['idle', 'loading'].includes(status);

    return (
        <div className='page px-4 py-3 d-flex flex-column overflow-y-auto'>
            <PageHeader
                title='Movies'
                addButtonAction={isAdmin(user) ? { navigateTo: "/movies/new" } : null}
                searchBar={{ searchValue: filterBy.search, handleSearch }}
            />

            <div className='contentContainer'>
                <div className="d-flex justify-content-start align-items-center gap-3">
                    <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                    <ButtonGroup className="gap-2">
                        <Button
                            color="primary"
                            outline={!filterBy.status.includes('published')}
                            size="sm"
                            className="rounded-3"
                            onClick={() => toggleStatus('published')}
                        >
                            {filterBy.status.includes('published') ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : (
                                <span className="fas fa-toggle-off me-2"/>
                            )}
                            Published
                        </Button>

                        <Button
                            color="primary"
                            outline={!filterBy.status.includes('draft')}
                            size="sm"
                            className="rounded-3"
                            onClick={() => toggleStatus('draft')}
                        >
                            {filterBy.status.includes('draft') ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : (
                                <span className="fas fa-toggle-off me-2"/>
                            )}
                            Draft
                        </Button>

                        <Button
                            color="primary"
                            outline={!filterBy.withoutVersions}
                            size="sm"
                            className="rounded-3"
                            onClick={toggleWithoutVersions}
                        >
                            {filterBy.withoutVersions ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : (
                                <span className="fas fa-toggle-off me-2"/>
                            )}
                            No Versions
                        </Button>

                        <Button
                            color="primary"
                            outline={!filterBy.withoutPosters}
                            size="sm"
                            className="rounded-3"
                            onClick={toggleWithoutPosters}
                        >
                            {filterBy.withoutPosters ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : (
                                <span className="fas fa-toggle-off me-2"/>
                            )}
                            No Poster
                        </Button>
                    </ButtonGroup>

                    <div className="flex-grow-1">
                        <span className="fas fa-list me-2 text-primary" />
                        showing {pagination.totalCount} result{pagination.totalCount === 1 ? '' : 's'}
                    </div>

                    <div className="text-primary">
                        <Button color="link" onClick={() => setDisplayList(!displayList)} className="text-decoration-none">
                            <span className={`fas ${displayList ? 'fa-table-cells' : 'fa-rectangle-list'} me-2`} />
                            {displayList ? 'Grid' : 'List'}
                        </Button>
                    </div>
                </div>

                {isLoading && <Loading />}

                {status === 'success' && movies.length > 0 && (
                    <Fragment>
                        <div className={displayList ? '' : 'movie-container d-flex flex-wrap'}>
                            {movies.map(movie => (
                                <NavLink
                                    to={`/movies/${movie._id}`}
                                    className={`text-decoration-none text-white${displayList ? '' : ' mx-3 mb-4'}`}
                                    key={movie._id}
                                >
                                    {displayList ? (
                                        <MovieCard movie={movie} hideStatus={!isAdmin(user)} />
                                    ) : (
                                        <MovieThumb movie={movie} animatedFooter />
                                    )}
                                </NavLink>
                            ))}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-center align-items-center gap-3 my-4">
                                <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    disabled={pagination.currentPage === 1}
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                >
                                    <span className="fas fa-chevron-left me-2" />
                                    Previous
                                </Button>

                                <span className="text-white">
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </span>

                                <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    disabled={!pagination.hasMore}
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                >
                                    Next
                                    <span className="fas fa-chevron-right ms-2" />
                                </Button>
                            </div>
                        )}
                    </Fragment>
                )}

                {status === 'success' && movies.length === 0 && (
                    <div>
                        No movies found.{' '}
                        {!areFiltersActive && (
                            <>
                                {' '}
                                <NavLink className="text-decoration-none" to="/movies/new">
                                    <span className="fas fa-plus me-2" />
                                    Add a new movie now
                                </NavLink>
                            </>
                        )}
                    </div>
                )}

                <div className="clearfix py-4" />
            </div>
        </div>
    );
};

export default Movies;
